import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useWorkspace } from "@/hooks/useWorkspace";
import { formatReadwiseTimestamp, READWISE_CATEGORIES } from "@/lib/readwise";
import { RefreshCw } from "lucide-react";
import { useReadwiseIntegration, type PurgeMode } from "./readwise/useReadwiseIntegration";
import { ReadwiseConnectDialog } from "./readwise/ReadwiseConnectDialog";
import { ReadwisePurgeDialog } from "./readwise/ReadwisePurgeDialog";

export const ReadwiseIntegrationCard = () => {
  const { active, activePersonaId } = useWorkspace();
  const {
    integration,
    integrationMeta,
    highlightCount,
    loading,
    busy,
    syncing,
    categories,
    savingCats,
    purging,
    connect,
    sync,
    disconnect,
    toggleCategory,
    purge,
  } = useReadwiseIntegration(active?.id ?? null, activePersonaId ?? null);

  const [open, setOpen] = useState(false);
  const [purgeMode, setPurgeMode] = useState<PurgeMode | null>(null);

  if (!active) return null;

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle>Readwise integration</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {loading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : integration ? (
          <>
            <div className="rounded-md border border-border bg-muted/20 p-3">
              <p className="font-medium text-foreground">{integrationMeta.summary}</p>
              <p className="mt-1 text-xs text-muted-foreground">{integrationMeta.detail}</p>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Status</span>
              <Badge variant={integrationMeta.tone === "destructive" ? "destructive" : "default"}>
                {integrationMeta.badgeLabel}
              </Badge>
            </div>
            {integration.error_message && (
              <p className="text-destructive text-xs">{integration.error_message}</p>
            )}
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Highlights imported</span>
              <span className="font-medium">{highlightCount.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Last sync</span>
              <span className="font-medium">{formatReadwiseTimestamp(integration.last_synced_at)}</span>
            </div>

            <div className="space-y-2 pt-2 border-t border-border">
              <div>
                <p className="font-medium text-foreground">Sync these categories</p>
                <p className="text-xs text-muted-foreground">
                  Supplementals and Tweets auto-purge after 7 days. Untick a category to stop importing it on the next sync.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {READWISE_CATEGORIES.map((cat) => {
                  const on = categories.includes(cat.key);
                  return (
                    <button
                      key={cat.key}
                      type="button"
                      onClick={() => toggleCategory(cat.key)}
                      disabled={savingCats}
                      className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                        on
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background text-muted-foreground border-border hover:bg-muted"
                      } disabled:opacity-50`}
                    >
                      {cat.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={sync} disabled={syncing || !integrationMeta.canSync} size="sm">
                <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? "Syncing…" : "Sync now"}
              </Button>
              {integrationMeta.needsReconnect ? (
                <Button onClick={() => setOpen(true)} disabled={busy} variant="outline" size="sm">
                  Reconnect token
                </Button>
              ) : null}
              <Button onClick={disconnect} disabled={busy} variant="outline" size="sm">
                Disconnect
              </Button>
            </div>

            <div className="space-y-2 pt-2 border-t border-border">
              <p className="font-medium text-foreground">Purge highlights</p>
              <p className="text-xs text-muted-foreground">
                Manual cleanup. Use this to clear out a category right now without waiting for the daily auto-purge.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setPurgeMode("supplementals")} variant="outline" size="sm">
                  Purge Supplementals
                </Button>
                <Button onClick={() => setPurgeMode("tweets")} variant="outline" size="sm">
                  Purge Tweets
                </Button>
                <Button onClick={() => setPurgeMode("all")} variant="destructive" size="sm">
                  Purge ALL Readwise
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <p className="text-muted-foreground">
              Connect your Readwise account to import all your highlights into Excerptus.
            </p>
            <Button onClick={() => setOpen(true)} size="sm">
              Connect Readwise
            </Button>
          </>
        )}
      </CardContent>

      <ReadwiseConnectDialog open={open} onOpenChange={setOpen} busy={busy} onConnect={connect} />
      <ReadwisePurgeDialog
        mode={purgeMode}
        onClose={() => setPurgeMode(null)}
        purging={purging}
        onConfirm={purge}
      />
    </Card>
  );
};
